// ============================================================================
// FILE: MasterSearchSelect.jsx
// PURPOSE: Universal ERP searchable dropdown (Value Help / LOV)
// ============================================================================

import React, { useEffect, useState, useRef } from "react";
import axiosClient from "../../api/axiosClient";

export default function MasterSearchSelect({
  type,
  materialType,
  extra = {},
  placeholder = "Search...",
  value,
  onChange
}) {

  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // ============================================================
  // SEARCH API CALL
  // ============================================================
  const search = async (text) => {

    if (!type) return;

    setLoading(true);

    try {
      const params = {
        type,
        query: text || "",
        ...extra
      };

      if (materialType) {
        params.material_type = materialType;
      }

      const res = await axiosClient.get(
       // "/api/core/master-search/search",
        "/api/master-search/search",
        { params }
      );

      setOptions(res.data.data || []);
    } catch (e) {
      console.error("Master search failed", e);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // DEBOUNCE SEARCH (300ms)
  // ============================================================
  useEffect(() => {

    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      search(query);
    }, 300);

    return () => clearTimeout(debounceRef.current);

  }, [query, materialType, JSON.stringify(extra)]);

  // ============================================================
  // CLOSE DROPDOWN OUTSIDE CLICK
  // ============================================================
  useEffect(() => {
    const handleClick = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // ============================================================
  // SELECT ITEM
  // ============================================================
  const selectItem = (item) => {
    onChange?.(item);
    setQuery(item.display);
    setOpen(false);
  };

  // ============================================================
  // UI
  // ============================================================
  return (
    <div
      ref={containerRef}
      style={{ position: "relative", minWidth: 220 }}
    >

      <input
        className="erp-filter"
        placeholder={placeholder}
        value={query}
        onFocus={() => {
          setOpen(true);
          search("");
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
      />

      {open && (
        <div
          style={{
            position: "absolute",
            top: 36,
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            maxHeight: 240,
            overflowY: "auto",
            zIndex: 50
          }}
        >
          {loading && (
            <div className="p-2 text-sm text-gray-500">
              Searching...
            </div>
          )}

          {!loading && options.length === 0 && (
            <div className="p-2 text-sm text-gray-400">
              No results
            </div>
          )}

          {options.map(opt => (
            <div
              key={opt.id}
              className="p-2 text-sm hover:bg-indigo-50 cursor-pointer"
              onClick={() => selectItem(opt)}
            >
              {opt.display}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
